import type { Vector3 } from 'three';
import type { StaticLight } from '../render/bake-light';
import { LIGHTS } from '../render/palette';
import type { MallPhysics } from './physics';

/**
 * HOW LIT IS THIS PATCH OF FLOOR?
 *
 * The mall is a building about light. Every shot in the reference sheet is a
 * pool of fluorescent white with a great deal of blue-grey nothing around it,
 * and until this file existed all of that was scenery: a thief standing under a
 * shop batten and a thief pressed into an unlit service alcove were noticed at
 * exactly the same rate. The most legible piece of cover in the game — the one
 * the player can see from across the concourse, with no HUD at all — did
 * nothing. That is the difference between a stealth game and a walking
 * simulator with a vision cone attached.
 *
 * So: darkness hides you, and it hides you by the same lights that were baked
 * into the walls. Sampling the bake itself would be the exact answer, but the
 * bake is per-vertex on surfaces and a player stands in the air between them.
 * Re-evaluating the light rig instead costs a handful of rays and has one
 * property the lightmap could not give: it agrees with what the player's eye is
 * being told, because it is the same list of lights with the same falloff.
 *
 * WHAT IT IS NOT. Not a light probe, not a bounce estimate, not physically
 * anything. It is a gameplay number in 0..1 that has to rise when the player
 * walks into a bright place and fall when they leave it, be stable enough that
 * they can learn a route, and be cheap enough to run on every thief on the
 * host. Accuracy past that buys nothing.
 */

/** Chest height. Sampling at the feet reads the shadow the player casts. */
const SAMPLE_Y = 1.15;

/**
 * How many of the nearest lights actually get a shadow ray.
 *
 * Fifty-three lights in the building and the answer never changes past the
 * closest few: a batten eleven metres away contributes under a hundredth of
 * what the one overhead does, and the cutoff sorting means the ones that matter
 * are always at the front. Six is the point where doubling it stopped moving
 * any sample in the mall by more than 0.01.
 */
const MAX_RAYS = 6;

/**
 * Irradiance that reads as fully exposed.
 *
 * MEASURED, not chosen — see `tools/light-census.ts`, which walks every seventh
 * node of the nav grid and prints the distribution. Across the walkable floor
 * the p50 sits near 0.14 and the p90 near 0.7; a shop floor directly under one
 * of its own battens works out around 0.55, and the moonlit forecourt about the
 * same. Putting the ceiling near 1.0 is what makes those two places read as the
 * bright ones rather than as an unremarkable middle.
 *
 * The first version of this file guessed 2.6 and the census came back with 2.5%
 * of the mall classed as lit. That is a mechanic with nothing to bite on, and
 * it is the reason the census exists.
 */
const FULL_BRIGHT = 1.0;

/**
 * The floor: hemisphere bounce and the directionless fill, both of which reach
 * everywhere including sealed rooms. Worked out from the rig rather than picked
 * — sky and fill together carry a luminance near 0.05 — because a generous
 * guess here is invisible and disastrous: at 0.22 it was larger than the median
 * real light in the building, so every sample was mostly ambient and nowhere
 * could be told apart from anywhere else.
 *
 * It is not zero because an unlit thief is still a shape against a grey floor.
 * Concealment is a discount, never a cloak.
 */
const AMBIENT = 0.05;

/**
 * THE MOON.
 *
 * The one light in the building that is not in the point-light list, and the
 * one the whole art direction is built on: the skylight wedge down the atrium,
 * the forecourt, the glazed shopfronts on the plaza side. Leaving it out meant
 * the census called the moonlit car park darker than a stock room, which is
 * both wrong and backwards — running across open tarmac has to be the most
 * exposed thing in the game, and it is the last thing every round asks you to
 * do.
 *
 * One extra ray, straight up at the sky. Inside, the roof eats it everywhere
 * except under the glazing, which is exactly the shape the player can see.
 */
const MOON_DIR = (() => {
  const [x, y, z] = LIGHTS.moon.position;
  const len = Math.hypot(x, y, z);
  return { x: x / len, y: y / len, z: z / len };
})();

/**
 * Moonlight's contribution when nothing is in the way.
 *
 * The rig's own luminance times its intensity, then discounted: the moon sits
 * at about 33° so it rakes across a standing body rather than lighting it
 * squarely, and unlike a batten overhead it only ever catches one side of you.
 */
const MOON_POWER = 0.34;

/** Far enough to leave the building from anywhere inside it. */
const MOON_REACH = 120;

const dir = { x: 0, y: 0, z: 0 };
const from = { x: 0, y: 0, z: 0 };

interface Lamp {
  x: number;
  y: number;
  z: number;
  /** Scalar radiance — the light's colour is irrelevant to being seen. */
  power: number;
  distance: number;
  decay: number;
}

/**
 * Samples how brightly lit a point is, against a fixed light rig.
 *
 * Built once from the same array the lightmapper was handed, so a fitting that
 * was never authored cannot light you and a fitting that was authored always
 * can. Holds no per-frame state: two calls with the same point return the same
 * number, which is what lets the host re-simulate.
 */
export class ExposureField {
  private readonly lamps: Lamp[] = [];
  /** Scratch, reused every sample so a per-tick call allocates nothing. */
  private readonly near: Array<{ lamp: Lamp; d2: number }> = [];

  constructor(
    lights: readonly StaticLight[],
    private readonly physics: MallPhysics,
  ) {
    for (const l of lights) {
      const p = l.position as Vector3;
      // Luminance rather than the mean: a sodium lamp and a cold batten of the
      // same wattage do not read as equally bright, and the eye the guard is
      // standing in for is the player's.
      const power = 0.2126 * l.radiance.x + 0.7152 * l.radiance.y + 0.0722 * l.radiance.z;
      if (power <= 0) continue;
      this.lamps.push({ x: p.x, y: p.y, z: p.z, power, distance: l.distance, decay: l.decay });
    }
  }

  get lampCount(): number {
    return this.lamps.length;
  }

  /**
   * Raw irradiance at a point, before it is squashed into 0..1.
   *
   * Separate from `exposure` because the calibration above is only meaningful
   * if there is a way to read the unscaled number off the real building — see
   * `__game.exposureCensus`.
   */
  irradiance(x: number, z: number, floorY: number): number {
    const y = floorY + SAMPLE_Y;
    this.near.length = 0;
    for (const lamp of this.lamps) {
      const dx = lamp.x - x;
      const dy = lamp.y - y;
      const dz = lamp.z - z;
      const d2 = dx * dx + dy * dy + dz * dz;
      // Outside its own authored cutoff a light contributes nothing at all, in
      // the shader and here. Skipping them first is also what keeps the sort
      // short in the parts of the map that have thirty lights in line of sight.
      if (d2 > lamp.distance * lamp.distance) continue;
      this.near.push({ lamp, d2 });
    }
    this.near.sort((a, b) => a.d2 - b.d2);

    let total = 0;
    const rays = Math.min(this.near.length, MAX_RAYS);
    for (let i = 0; i < rays; i++) {
      const { lamp, d2 } = this.near[i];
      const d = Math.sqrt(d2);
      if (d < 1e-3) {
        total += lamp.power;
        continue;
      }
      // three's cutoff falloff, matched so a light dies here exactly where it
      // dies on screen rather than a metre either side of it.
      const t = Math.min(d / lamp.distance, 1);
      const window = Math.max(1 - t * t * t * t, 0) ** 2;
      const fall = window / Math.max(d ** lamp.decay, 1e-4);
      const contribution = lamp.power * fall;
      // Not worth a ray. This is the common case by a wide margin.
      if (contribution < 0.02) continue;

      from.x = x;
      from.y = y;
      from.z = z;
      dir.x = (lamp.x - x) / d;
      dir.y = (lamp.y - y) / d;
      dir.z = (lamp.z - z) / d;
      // Cast from the player to the lamp, not the other way: the ray filter
      // already excludes actors, and starting at the light would mean the first
      // thing every ray hits is the ceiling the fitting is screwed to.
      const toi = this.physics.ray(from, dir, d - 0.25);
      if (toi === null) total += contribution;
    }

    from.x = x;
    from.y = y;
    from.z = z;
    if (this.physics.skyRay(from, MOON_DIR, MOON_REACH)) total += MOON_POWER;
    return total;
  }

  /**
   * The gameplay number. Zero is as dark as the building gets; one is standing
   * directly under a fitting with nothing between.
   */
  exposure(x: number, z: number, floorY: number): number {
    const raw = this.irradiance(x, z, floorY) + AMBIENT;
    return Math.min(raw / FULL_BRIGHT, 1);
  }
}
